/**
 * useChatRouting — the SINGLE source of truth for the active chat session id.
 *
 * The active-session identity used to be smeared across five places (the URL
 * param, `internalSessionId`, `localOverride`, `useChatSession`'s
 * `session.sessionId`, and the React-Query `sessionDetail`) kept in sync by a
 * tangle of ref guards and racing effects. This hook collapses the routing half
 * of that into one owner with a synchronous setter:
 *
 *   - It owns `sessionId` as local state, so writes are visible on the very next
 *     render (no optimistic `localOverride` needed).
 *   - In CONTROLLED mode it mirrors the id to an external URL param
 *     (GlobalAIButton's `?luma=`, page panels' `?chat=`) via
 *     `onExternalSessionChange`, and adopts EXTERNAL url changes it didn't make
 *     (deep links, history-sidebar navigation, browser back/forward).
 *   - A single `lastWrittenRef` distinguishes the async echo of our OWN url
 *     write (id → URL → prop round-trip) from a genuine external change, so we
 *     never clobber a freshly-set id while the URL catches up. This is what the
 *     removed `localOverride` + drop-override effect + URL "heal/re-sync" effect
 *     were hand-rolling.
 *   - In UNCONTROLLED mode (no external param) the id lives purely in local
 *     state.
 *
 * The owned `sessionId` is the ONE input handed to `useChatSession`, whose
 * observer WebSocket connects/disconnects as a deterministic function of it.
 */
import { useState, useRef, useEffect, useCallback } from 'react';

interface UseChatRoutingOptions {
  /**
   * Controlled mode: the active session id as held by an external URL param.
   * `undefined` selects UNCONTROLLED mode (id lives only in local state);
   * `string | null` selects CONTROLLED mode.
   */
  externalSessionId?: string | null;
  /** Controlled mode: mirror a newly-set id back to the URL. */
  onExternalSessionChange?: (sessionId: string | null) => void;
  /**
   * When this key changes, the active session resets to `null`. Used to drop
   * the bound session when the panel re-targets a different entity (entityRef).
   */
  resetKey?: string;
}

interface UseChatRoutingReturn {
  /** The single source of truth for the active session id. */
  sessionId: string | null;
  /**
   * Synchronous setter — updates local state immediately (visible next render)
   * and, in controlled mode, mirrors the id to the URL.
   */
  setSessionId: (sessionId: string | null) => void;
}

export function useChatRouting({
  externalSessionId,
  onExternalSessionChange,
  resetKey,
}: UseChatRoutingOptions): UseChatRoutingReturn {
  const isControlled = externalSessionId !== undefined;

  const [sessionId, setLocalSessionId] = useState<string | null>(
    isControlled ? externalSessionId ?? null : null,
  );

  // The last id WE wrote (locally + to the URL). Lets the reconciliation effect
  // ignore the async echo of our own write while still adopting genuine
  // EXTERNAL changes.
  const lastWrittenRef = useRef<string | null>(sessionId);

  const setSessionId = useCallback(
    (next: string | null) => {
      lastWrittenRef.current = next;
      setLocalSessionId(next);
      if (isControlled) {
        onExternalSessionChange?.(next);
      }
    },
    [isControlled, onExternalSessionChange],
  );

  // Adopt EXTERNAL url changes that didn't originate from our own setSessionId.
  // Our own writes echo back identically (incoming === lastWrittenRef) and are
  // skipped here — that echo suppression is why a just-set id survives the URL
  // round-trip without an optimistic override.
  useEffect(() => {
    if (!isControlled) return;
    const incoming = externalSessionId ?? null;
    if (incoming === lastWrittenRef.current) return;
    lastWrittenRef.current = incoming;
    setLocalSessionId(incoming);
  }, [isControlled, externalSessionId]);

  // Reset the active session when the panel re-targets a new entity.
  const prevResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;
    setSessionId(null);
  }, [resetKey, setSessionId]);

  return { sessionId, setSessionId };
}
